<template>
  <div class="stacked-document-display">
    <div v-if="documents.length > 0" class="documents-stack">
      <!-- Loop through all documents and display them -->
      <div
        v-for="(document, index) in documents"
        :key="index"
        class="document-item"
      >
        <div class="document-container">
          <!-- Document Preview -->
          <div class="document-preview">
            <!-- Show video if it's an MP4/MOV file -->
            <div class="video-container" v-if="isVideoFile(document.type) && (document.preview || document.dataUrl)">
              <video
                autoplay
                controls
                loop
                muted
                class="document-video"
                :src="document.preview || document.dataUrl"
                :alt="document.name"
                @error="handleMediaError"
              ></video>

              <!-- Add Live Transcript component -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
              />
            </div>

            <!-- If it's a video but we DON'T have preview data, show name-based placeholder -->
            <div v-else-if="isVideoFile(document.type)" class="video-placeholder">
              <div class="placeholder-icon">🎥</div>
              <div class="video-name">{{ document.name }}</div>
              <div class="video-info">Video preview not available</div>

              <!-- Add Live Transcript component for placeholder too -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
              />
            </div>

            <!-- Show image with animation if it's an image and we have preview data -->
            <div class="image-container animated" v-else-if="isImageFile(document.type) && (document.preview || document.dataUrl)">
              <div class="image-animation-container" :class="randomAnimation">
                <img
                  :src="document.preview || document.dataUrl"
                  :alt="document.name"
                  class="document-image"
                  @error="handleMediaError"
                />
              </div>

              <!-- Optional overlay effects -->
              <div class="image-overlay" v-if="showOverlayEffects">
                <div class="overlay-element element-1"></div>
                <div class="overlay-element element-2"></div>
                <div class="overlay-element element-3"></div>
              </div>

              <!-- Add Live Transcript component -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
              />
            </div>

            <!-- If it's an image but we DON'T have preview data, show name-based placeholder -->
            <div v-else-if="isImageFile(document.type)" class="image-placeholder">
              <div class="placeholder-icon">🖼️</div>
              <div class="image-name">{{ document.name }}</div>
              <div class="image-info">Image preview not available</div>

              <!-- Add Live Transcript component for placeholder too -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
              />
            </div>

            <!-- Non-image, non-video document (PDF, DOCX, etc) -->
            <div v-else class="file-icon-container">
              <div class="file-icon" :class="getFileIconClass(document.type)">
                {{ getFileExtension(document.name).toUpperCase() }}
              </div>
              <div class="document-name">{{ document.name }}</div>

              <!-- Add Live Transcript component for document files -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No documents placeholder -->
    <div v-else class="no-documents">
      <div class="placeholder-icon">📄</div>
      <p>No documents available</p>

      <!-- Add Live Transcript component for empty state -->
      <live-transcript
        v-show="isPlaying"
        :voice-speed="voiceSpeed"
        :text="activeSegmentContent"
        :speaker="activeSegmentSpeaker"
        :speaker-image="speakerImage"
        :visible="isPlaying"
        :message-id="messageId"
        :background-color="transcriptBackgroundColor"
      />
    </div>
  </div>
</template>

<script>
import LiveTranscript from '../components/LiveTranscript.vue';

export default {
  name: 'StackedDocumentDisplay',
  components: {
    LiveTranscript
  },
  props: {
    messageId: {
      required: true,
    },
    voiceSpeed: {
      type: Number,
      default: 1
    },
    speakerImage: {
      type: String,
      default: '/static/Gemini_Generated_Image_uobiifuobiifuobi.jpeg'
    },
    documents: {
      type: Array,
      default: () => []
    },
    isPlaying: {
      type: Boolean,
      default: false
    },
    activeSegmentContent: {
      type: String,
      default: ''
    },
    activeSegmentSpeaker: {
      type: String,
      default: ''
    },
    transcriptBackgroundColor: {
      type: String,
      default: 'rgba(0, 0, 0, 0.7)' // Default black with 0.7 opacity
    }
  },
  data() {
    return {
      animations: [
        'zoom-pan-effect',
        'ken-burns-effect',
        'fade-and-scale',
        'gentle-float',
        'smooth-slide'
      ],
      showOverlayEffects: true,
      animationIndex: 0
    };
  },
  computed: {
    randomAnimation() {
      return this.animations[this.animationIndex % this.animations.length];
    }
  },
  watch: {
    // Watch for changes to the transcript background color prop
    transcriptBackgroundColor: {
      immediate: true,
      handler(newColor) {
        // We don't need to do anything in the handler,
        // just having the watcher ensures Vue will trigger updates
        // for all child components that use this prop

        // If needed, we could force a re-render:
        this.$forceUpdate();
      }
    }
  },
  methods: {
    isImageFile(fileType) {
      return fileType && fileType.startsWith('image/');
    },

    isVideoFile(fileType) {
      return fileType && (
        fileType.startsWith('video/') ||
        fileType.includes('mp4') ||
        fileType.includes('mov') ||
        fileType.includes('quicktime') ||
        fileType.includes('webm') ||
        fileType.includes('ogg')
      );
    },

    getFileExtension(filename) {
      if (!filename) return '';
      return filename.split('.').pop() || '';
    },

    getFileIconClass(fileType) {
      if (!fileType) return 'generic-icon';
      if (fileType.includes('pdf')) return 'pdf-icon';
      if (fileType.includes('word') || fileType.includes('doc')) return 'doc-icon';
      if (fileType.includes('excel') || fileType.includes('sheet') || fileType.includes('xls')) return 'xls-icon';
      if (fileType.includes('powerpoint') || fileType.includes('presentation') || fileType.includes('ppt')) return 'ppt-icon';
      if (this.isVideoFile(fileType)) return 'video-icon';
      return 'generic-icon';
    },

    handleMediaError(e) {
      console.warn('Media load error:', e.target.alt);
      e.target.style.display = 'none';

      // Create a placeholder
      const placeholder = document.createElement('div');
      placeholder.className = 'file-icon generic-icon';
      placeholder.textContent = this.getFileExtension(e.target.alt).toUpperCase();
      e.target.parentNode.appendChild(placeholder);

      // Add a message about the failed load
      const errorMsg = document.createElement('div');
      errorMsg.className = 'media-error-message';
      errorMsg.textContent = `${e.target.tagName === 'VIDEO' ? 'Video' : 'Image'} could not be loaded`;
      e.target.parentNode.appendChild(errorMsg);
    },

    // Change animation every 8 seconds
    startAnimationCycle() {
      this.animationTimer = setInterval(() => {
        this.animationIndex++;
      }, 8000);
    }
  },
  mounted() {
    this.startAnimationCycle();
  },
  beforeDestroy() {
    if (this.animationTimer) {
      clearInterval(this.animationTimer);
    }
  }
}
</script>

<style scoped>
.stacked-document-display {
  width: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  position: relative;
}

.documents-counter {
  text-align: center;
  padding: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  background-color: rgba(4, 255, 134, 0.1);
  border-radius: 999px;
  display: inline-block;
}

.documents-stack {
  display: flex;
  flex-direction: column;
}

.document-item {
  position: relative;
}

.document-number {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: rgba(4, 255, 134, 0.8);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  z-index: 2;
}

.document-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  border-radius: 8px;
  overflow: hidden;
  background-color: rgba(0, 0, 0, 0.3);
  position: relative;
  padding: 0rem 1rem 1rem 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.document-preview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

/* Image styles */
.image-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.image-animation-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.document-image {
  width: 100%;
  max-height: calc(100vh - 200px);
  object-fit: contain;
  border-radius: 4px;
}

.zoom-pan-effect {
  animation: zoomPan 15s ease-in-out infinite;
}

.ken-burns-effect {
  animation: kenBurns 20s ease-in-out infinite;
}

.fade-and-scale {
  animation: fadeScale 12s ease-in-out infinite;
}

.gentle-float {
  animation: gentleFloat 10s ease-in-out infinite;
}

.smooth-slide {
  animation: smoothSlide 18s ease-in-out infinite;
}

/* Updated keyframes with smoother loops */
@keyframes zoomPan {
  0%, 100% {
    transform: scale(1) translate(0, 0);
  }
  50% {
    transform: scale(1.1) translate(-2%, -1%);
  }
}

@keyframes kenBurns {
  0%, 100% {
    transform: scale(1.05) translate(-1%, -1%);
  }
  25% {
    transform: scale(1.09) translate(0%, 1%);
  }
  50% {
    transform: scale(1.12) translate(1%, 1%);
  }
  75% {
    transform: scale(1.09) translate(0%, 0%);
  }
}

@keyframes fadeScale {
  0%, 100% {
    opacity: 0.95;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
  }
}

@keyframes gentleFloat {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes smoothSlide {
  0%, 100% {
    transform: translateX(-2%) scale(1.05);
  }
  50% {
    transform: translateX(2%) scale(1.05);
  }
}

/* Improved overlay effects */
@keyframes drift1 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(10%, 10%) scale(1.1); }
}

@keyframes drift2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-10%, -5%) scale(0.9); }
}

@keyframes drift3 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(5%, -8%) scale(1.05); }
}

/* Optional overlay effects */
.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.overlay-element {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  filter: blur(8px);
}

.element-1 {
  width: 40%;
  height: 40%;
  top: -10%;
  left: -10%;
  animation: drift1 25s infinite alternate;
}

.element-2 {
  width: 25%;
  height: 25%;
  bottom: 10%;
  right: 5%;
  animation: drift2 20s infinite alternate;
}

.element-3 {
  width: 35%;
  height: 35%;
  bottom: -15%;
  left: 30%;
  animation: drift3 30s infinite alternate;
}

@keyframes drift1 {
  0% { transform: translate(0, 0) scale(1); }
  100% { transform: translate(10%, 10%) scale(1.1); }
}

@keyframes drift2 {
  0% { transform: translate(0, 0) scale(1); }
  100% { transform: translate(-10%, -5%) scale(0.9); }
}

@keyframes drift3 {
  0% { transform: translate(0, 0) scale(1); }
  100% { transform: translate(5%, -8%) scale(1.05); }
}

.image-placeholder {
  width: 100%;
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 1rem;
  position: relative;
}

.video-placeholder {
  width: 100%;
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 1rem;
  position: relative;
}

/* Video styles */
.video-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.document-video {
  width: 100%;
  max-height: calc(100vh - 200px);
  border-radius: 4px;
}

/* Common styles */
.placeholder-icon {
  font-size: 3rem;
  margin-bottom: 0.5rem;
  opacity: 0.7;
}

.image-name, .video-name {
  font-weight: 500;
  text-align: center;
  margin-bottom: 0.5rem;
  max-width: 100%;
  word-break: break-word;
}

.image-info, .video-info {
  font-size: 0.875rem;
  color: #999;
  text-align: center;
}

.media-error-message {
  color: #ff6b6b;
  font-size: 0.75rem;
  text-align: center;
  margin-top: 0.5rem;
}

.file-icon-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  position: relative;
  width: 100%;
}

.file-icon {
  width: 120px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 2rem;
  color: white;
  position: relative;
}

.file-icon:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #e0e0e0;
  clip-path: polygon(0% 0%, 80% 0%, 100% 20%, 100% 100%, 0% 100%);
}

.file-icon:after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 20px;
  background: #c0c0c0;
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%);
}

.pdf-icon:before { background: #FF5722; }
.doc-icon:before { background: #2196F3; }
.xls-icon:before { background: #4CAF50; }
.ppt-icon:before { background: #FF9800; }
.video-icon:before { background: #E91E63; }
.generic-icon:before { background: #9E9E9E; }

.document-name {
  font-size: 1rem;
  text-align: center;
  max-width: 300px;
  word-break: break-word;
  margin-top: 1rem;
}

.no-documents {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #999;
  position: relative;
  min-height: 200px;
}

@media (max-width: 768px) {
  .document-container {
    max-height: calc(100vh - 150px);
  }

  .document-image, .document-video {
    max-height: calc(100vh - 150px);
  }

  .file-icon {
    width: 80px;
    height: 100px;
    font-size: 1.5rem;
  }
}

@media (max-width: 768px) {
  .stacked-document-display {
    margin-bottom: 60px; /* Space for transcript below */
    overflow: visible; /* Ensure transcript is visible */
  }

  /* Modify the LiveTranscript only when inside the document display */
  .stacked-document-display .live-transcript-overlay {
    position: relative;
    bottom: auto;
    margin-top: 10px;
  }

  /* Make transcript container full width on mobile */
  .stacked-document-display .live-transcript-container {
    max-width: 100%;
  }
}
</style>
